/**
 * DMARC utilities — merged from dmarcAnalysis.ts and dmarcExplanations.ts.
 *
 * Covers two concerns that belong together:
 *   1. Analysis helpers: interfaces and logic for explaining quarantine/rejection reasons.
 *   2. Explanation helpers: user-friendly labels, tooltips, and glossary for DMARC terminology.
 */

import type { ViewMode } from '../context/ViewModeContext'

// ─── Analysis (formerly dmarcAnalysis.ts) ────────────────────────────────────

export interface QuarantineReason {
  category: 'policy' | 'authentication' | 'alignment' | 'percentage'
  severity: 'high' | 'medium' | 'low'
  message: string
  technicalDetails?: string
}

export interface DMARCRecordForAnalysis {
  Disposition?: string
  DKIMResult?: string
  SPFResult?: string
  AuthSPFDomain?: string
  AuthDKIMDomain?: string
  // Server-derived: passing entry aligned with header_from (RFC 9989 §3.1.1 /
  // §3.1.2). Empty when none. Auth*Domain are raw entry [0] (AUD-13).
  AlignedDKIMDomain?: string
  AlignedSPFDomain?: string
  SourceIP: string
  PolicyP?: { String: string; Valid: boolean }
  PolicyADKIM?: { String: string; Valid: boolean }
  PolicyASPF?: { String: string; Valid: boolean }
  PolicyDomain?: { String: string; Valid: boolean }
  PolicyPCT?: { Int64: number; Valid: boolean }
  HumanResultDKIM?: { String: string; Valid: boolean }
  HeaderFromDomain?: string
}

/** RFC 1035 §2.3.3: case-insensitive; a trailing root dot names the same domain. */
function normDomain(d: string): string {
  return d.trim().toLowerCase().replace(/\.$/, '')
}

/**
 * The domain to attribute a DKIM/SPF result to: the server-derived aligned
 * (passing) domain when present, else the raw auth_results entry [0]
 * (reporter-ordered, deprecated for attribution — AUD-13).
 */
export function effectiveAuthDomain(aligned?: string, raw?: string): string {
  return aligned || raw || ''
}

/**
 * Explains why an email was quarantined or rejected based on DMARC record data
 * @param record DMARC record to analyze
 * @returns Array of reasons explaining the quarantine/rejection
 */
export function explainQuarantine(
  record: DMARCRecordForAnalysis
): QuarantineReason[] {
  const reasons: QuarantineReason[] = []

  // 1. DMARC Policy
  if (record.PolicyP?.Valid && record.PolicyP.String === 'quarantine') {
    reasons.push({
      category: 'policy',
      severity: 'high',
      message: 'Domain policy requires quarantine for failed authentication',
      technicalDetails: `Your DMARC record has p=quarantine, which means emails that fail authentication are moved to spam/junk folder`,
    })
  }

  // 2. Authentication Failures
  const dkimFailed = record.DKIMResult && record.DKIMResult !== 'pass'
  const spfFailed = record.SPFResult && record.SPFResult !== 'pass'

  if (dkimFailed && spfFailed) {
    reasons.push({
      category: 'authentication',
      severity: 'high',
      message: 'Both DKIM and SPF failed - email not authenticated',
      technicalDetails:
        'At least one authentication method (DKIM or SPF) must pass to deliver emails successfully. Add the sending IP to SPF record or configure DKIM signing.',
    })
  } else if (dkimFailed) {
    reasons.push({
      category: 'authentication',
      severity: 'medium',
      message: 'DKIM signature missing or invalid',
      technicalDetails: record.HumanResultDKIM?.Valid
        ? record.HumanResultDKIM.String
        : 'Email server needs to sign messages with DKIM. Configure DKIM keys in your email service.',
    })
  } else if (spfFailed) {
    reasons.push({
      category: 'authentication',
      severity: 'medium',
      message: `Email sent from unauthorized server (${record.SourceIP})`,
      technicalDetails: `Add this IP to your SPF record. Current SPF record does not authorize ${record.SourceIP} to send for ${record.AuthSPFDomain || 'this domain'}`,
    })
  }

  // 3. Alignment Failures (only if authentication passed but alignment failed)
  if (record.PolicyADKIM?.Valid && record.PolicyADKIM.String === 's') {
    // Strict DKIM alignment
    const dkimDomain = effectiveAuthDomain(record.AlignedDKIMDomain, record.AuthDKIMDomain)
    if (
      dkimDomain &&
      record.HeaderFromDomain &&
      normDomain(dkimDomain) !== normDomain(record.HeaderFromDomain)
    ) {
      reasons.push({
        category: 'alignment',
        severity: 'medium',
        message: 'DKIM domain mismatch (strict alignment required)',
        technicalDetails: `Email "From" header shows "${record.HeaderFromDomain}" but DKIM signature is for "${record.AuthDKIMDomain}". Either use relaxed alignment (adkim=r) or sign with matching domain.`,
      })
    }
  }

  if (record.PolicyASPF?.Valid && record.PolicyASPF.String === 's') {
    // Strict SPF alignment
    const spfDomain = effectiveAuthDomain(record.AlignedSPFDomain, record.AuthSPFDomain)
    if (
      spfDomain &&
      record.HeaderFromDomain &&
      normDomain(spfDomain) !== normDomain(record.HeaderFromDomain)
    ) {
      reasons.push({
        category: 'alignment',
        severity: 'medium',
        message: 'SPF domain mismatch (strict alignment required)',
        technicalDetails: `Email "From" header shows "${record.HeaderFromDomain}" but SPF check passed for "${record.AuthSPFDomain}". Either use relaxed alignment (aspf=r) or ensure domains match.`,
      })
    }
  }

  // 4. Percentage Sampling
  if (record.PolicyPCT?.Valid && record.PolicyPCT.Int64 < 100) {
    reasons.push({
      category: 'percentage',
      severity: 'low',
      message: `Only ${record.PolicyPCT.Int64}% of messages are subject to DMARC policy`,
      technicalDetails:
        'This email was randomly selected for policy enforcement based on pct setting',
    })
  }

  return reasons
}

// ─── Explanations (formerly dmarcExplanations.ts) ────────────────────────────

/**
 * User-friendly explanations for DMARC terminology
 * Based on docs/ux/personas.md - translating technical jargon to simple language
 */

/**
 * Get user-friendly disposition label
 */
export const getDispositionLabel = (disposition: string): string => {
  const d = disposition?.toLowerCase()
  switch (d) {
    case 'none':
      return '✅ Not blocked'
    case 'quarantine':
      return '⚠️ Sent to spam folder'
    case 'reject':
      return '❌ Blocked — never delivered'
    default:
      return disposition || 'Unknown'
  }
}

/**
 * Get detailed disposition explanation
 */
export const getDispositionExplanation = (disposition: string): string => {
  const d = disposition?.toLowerCase()
  switch (d) {
    case 'none':
      return "The receiving server applied no DMARC action to this email. For mail that passed authentication this is normal; for failing mail it means your policy didn't trigger (monitoring mode, partial rollout, or a receiver override). Inbox vs spam is decided by the receiver's own filters — DMARC reports don't include placement."
    case 'quarantine':
      return "Email was placed in the recipient's spam or junk folder"
    case 'reject':
      return 'Email was blocked and never delivered to the recipient'
    default:
      return 'Unknown disposition status'
  }
}

/**
 * Explain empty Auth DKIM field
 */
export const getEmptyAuthDKIMExplanation = (): string => {
  return "No DKIM signature found. This email was sent without a DKIM signature. This is normal for servers that don't sign messages. The email can still be delivered if SPF passes."
}

/**
 * Explain empty Auth SPF field
 */
export const getEmptyAuthSPFExplanation = (): string => {
  return 'No SPF domain found. The email may have been sent from a server without proper SPF configuration.'
}

/**
 * Get label for empty Auth DKIM
 */
export const getEmptyAuthDKIMLabel = (): string => {
  return '(no DKIM signature)'
}

/**
 * Get label for empty Auth SPF
 */
export const getEmptyAuthSPFLabel = (): string => {
  return '(no SPF domain)'
}

// ─── Canonical DMARC-auth-fail predicate ──────────────────────────────────────

/**
 * The canonical "unauthorized sender" dichotomy per RFC 9989: DMARC
 * passes when EITHER aligned mechanism (SPF or DKIM) passes. DKIMResult/
 * SPFResult here are the policy_evaluated (aligned) results, so a message
 * only fails DMARC when BOTH are not-pass — this deliberately covers 'fail',
 * '', undefined, 'temperror', and 'permerror' alike (not-pass is not-pass),
 * matching how the "Unauthorized sender" badge is decided.
 *
 * Single source of truth — used by both the DomainRecordRow badge and
 * isImpostorRow below so they never disagree on the same row.
 */
export function isDmarcAuthFail(r: {
  DKIMResult?: string
  SPFResult?: string
}): boolean {
  return r.DKIMResult !== 'pass' && r.SPFResult !== 'pass'
}

/**
 * Returns the "Unauthorized sender" badge tooltip, hedged when either result
 * is a transient/permanent DNS lookup error (temperror/permerror) or simply
 * missing rather than a definitive 'fail' — a DNS hiccup is not evidence of
 * an unauthorized sender, so don't assert it as fact.
 */
export function getUnauthorizedSenderTooltip(r: {
  DKIMResult?: string
  SPFResult?: string
}): string {
  const bothLiterallyFailed = r.DKIMResult === 'fail' && r.SPFResult === 'fail'
  if (bothLiterallyFailed) {
    return 'Both SPF and DKIM failed verification — this sender is not authorized to send email as your domain'
  }
  return "This email couldn't be verified — SPF and DKIM did not pass (verification errors or missing results). Treat as unauthenticated."
}

// ─── Impostor-row detection ────────────────────────────────────────────────────

/**
 * An impostor row is one where BOTH SPF and DKIM fail to pass (see
 * isDmarcAuthFail) AND neither produced an auth domain — i.e. the sender
 * isn't one of your services that simply has a misconfiguration; it's a
 * completely unrecognised sender pretending to be your domain.
 *
 * Conservative by design: a row with a present authDomain (alignment failure)
 * is NOT classified as an impostor so Suggest Fix remains available for it.
 */
export function isImpostorRow(r: {
  SPFResult?: string
  DKIMResult?: string
  AuthSPFDomain?: string
  AuthDKIMDomain?: string
}): boolean {
  return isDmarcAuthFail(r) && !r.AuthSPFDomain && !r.AuthDKIMDomain
}

// ─── Impostor-row policy guidance ─────────────────────────────────────────────

type OverrideGroup = 'fwd_list' | 'local' | 'none'

const FWD_LIST_TYPES = new Set([
  'forwarded',
  'mailing_list',
  'trusted_forwarder',
  'arc_pass',
])
const LOCAL_TYPES = new Set(['local_policy'])

function getOverrideGroup(policyOverrideReasons?: {
  String: string
  Valid: boolean
}): OverrideGroup {
  if (!policyOverrideReasons?.Valid || !policyOverrideReasons.String)
    return 'none'
  try {
    const reasons = JSON.parse(policyOverrideReasons.String) as Array<{
      type: string
    }>
    if (!Array.isArray(reasons) || reasons.length === 0) return 'none'
    for (const reason of reasons) {
      if (FWD_LIST_TYPES.has(reason.type)) return 'fwd_list'
    }
    for (const reason of reasons) {
      if (LOCAL_TYPES.has(reason.type)) return 'local'
    }
    return 'none'
  } catch {
    return 'none'
  }
}

export type ImpostorGuidance = {
  stripCopy: string
  ctaLabel: string | null
  ctaTarget: string | null
}

/**
 * Returns the inline amber strip copy + optional CTA for an impostor row.
 *
 * Returns `null` for the WIN case (disposition=reject on an impostor row) —
 * the caller should render only the badge + tooltip in that case, no strip.
 *
 * Decision tree follows the spec exactly; first match wins within each section.
 */
/**
 * D1: Simple-mode CTA label used whenever the Expert CTA routes to the DNS
 * tab to tighten enforcement — the underlying action ("go strengthen your
 * DMARC policy") is the same regardless of which specific policy step Expert
 * mode names (p=reject, pct=100, etc.), so one plain imperative covers all
 * of them without leaking `p=`/`pct` tokens.
 */
const SIMPLE_TURN_ON_BLOCKING_CTA = 'Turn on blocking →'

export function getImpostorPolicyGuidance(
  r: {
    Disposition?: string
    PolicyP?: { String: string; Valid: boolean }
    PolicyPCT?: { Int64: number; Valid: boolean }
    PolicyOverrideReasons?: { String: string; Valid: boolean }
    ReporterOrg?: { String: string; Valid: boolean }
  },
  domainId: string,
  viewMode: ViewMode = 'expert'
): ImpostorGuidance | null {
  const disposition = r.Disposition?.toLowerCase() ?? 'none'
  const ctaDns = `/dashboard/domains/${domainId}/dns`
  const simple = viewMode === 'simple'

  // Rule 1: missing / empty policy → treat as p=none
  const policyStr =
    r.PolicyP?.Valid && r.PolicyP.String ? r.PolicyP.String : 'none'

  // Rule 2: WIN — blocked impostor is a positive outcome, no strip needed
  if (disposition === 'reject') return null

  const pct = r.PolicyPCT?.Valid ? r.PolicyPCT.Int64 : 100
  const overrideGroup = getOverrideGroup(r.PolicyOverrideReasons)
  const provider =
    r.ReporterOrg?.Valid && r.ReporterOrg.String
      ? r.ReporterOrg.String
      : 'This provider'

  // ── p=none ──────────────────────────────────────────────────────────────────
  if (policyStr === 'none') {
    if (disposition === 'none' || disposition === '') {
      return {
        stripCopy: simple
          ? "This fake email got through because your protection isn't turned on yet. Turn it on to block these automatically."
          : "This sender failed every authentication check, but your 'monitor only' policy (p=none) lets it through. Gmail, Outlook, and every other provider will keep delivering these until you tell them to stop.",
        ctaLabel: simple ? SIMPLE_TURN_ON_BLOCKING_CTA : 'Switch to p=reject →',
        ctaTarget: ctaDns,
      }
    }
    if (disposition === 'quarantine') {
      return {
        stripCopy: simple
          ? "This fake email was caught by spam filters this time — but your protection isn't turned on, so another mail provider might let the same thing straight into the inbox. Turn it on to block these everywhere."
          : "Caught in spam — but only because this provider decided to. Your policy is 'monitor only' (p=none), so another provider could deliver the same mail straight to the inbox. Switch to p=reject to give every provider the same instruction.",
        ctaLabel: simple ? SIMPLE_TURN_ON_BLOCKING_CTA : 'Switch to p=reject →',
        ctaTarget: ctaDns,
      }
    }
  }

  // ── p=quarantine ─────────────────────────────────────────────────────────────
  if (policyStr === 'quarantine') {
    if (disposition === 'none' || disposition === '') {
      if (pct < 100) {
        return {
          stripCopy: simple
            ? "Your spam-folder protection doesn't cover all your mail yet — some of it gets no protection at all, and this one slipped through. Cover all of it, then consider full blocking to stop this outright."
            : `Your policy says 'spam folder' (p=quarantine), but this went through unblocked. Your policy only applies to ${pct}% of mail — the rest gets no enforcement at all. Raise pct to 100, then consider upgrading to p=reject to block this outright.`,
          ctaLabel: simple ? 'Cover all your mail →' : 'Tighten your policy →',
          ctaTarget: ctaDns,
        }
      }
      if (overrideGroup === 'fwd_list') {
        return {
          stripCopy: simple
            ? 'This was a forwarded or mailing-list message — mail providers often relax protection in those cases. This is expected; there is nothing to fix here.'
            : "Your policy says 'spam folder', but this went through unblocked anyway. It was a forwarded or mailing-list message — providers typically relax DMARC enforcement in those cases. This is expected; no policy change will fix it.",
          ctaLabel: null,
          ctaTarget: null,
        }
      }
      if (overrideGroup === 'local') {
        return {
          stripCopy: simple
            ? `Your spam-folder protection is on, but ${provider} used its own rules to deliver this anyway. Nothing to change on your end — this is ${provider}'s decision.`
            : `Your policy says 'spam folder', but ${provider} delivered this using its own rules. Some providers override DMARC for mail they trust. Nothing to change on your end — this is ${provider}'s decision.`,
          ctaLabel: null,
          ctaTarget: null,
        }
      }
      // pct=100, no override
      return {
        stripCopy: simple
          ? 'This fake email should have gone to spam, but it got through anyway — the provider may not have honored your setting. Turning on full blocking gives providers a stronger instruction to follow.'
          : "Your policy says 'spam folder' (p=quarantine), but this still went through unblocked. The provider may not have honored your policy. Upgrading to p=reject gives the strongest possible instruction — providers that ignore quarantine sometimes still enforce reject.",
        ctaLabel: simple ? 'Turn on full blocking →' : 'Upgrade to p=reject →',
        ctaTarget: ctaDns,
      }
    }
    if (disposition === 'quarantine') {
      return {
        stripCopy: simple
          ? "This fake email is sitting in someone's spam folder — one click away from a victim who thinks it's real. Turn on full blocking to stop it before it's even delivered."
          : "Filtered, not blocked. This forged email is sitting in someone's spam folder — one click away from a victim who thinks it's real. Upgrade to p=reject to stop it before delivery.",
        ctaLabel: 'Block it completely →',
        ctaTarget: ctaDns,
      }
    }
  }

  // ── p=reject ─────────────────────────────────────────────────────────────────
  if (policyStr === 'reject') {
    if (disposition === 'none' || disposition === '') {
      if (pct < 100) {
        return {
          stripCopy: simple
            ? `Your blocking protection doesn't cover all your mail yet — this one slipped through the uncovered part. Cover all of it to close the gap.`
            : `Your policy says 'block' (p=reject), but this got through. Your policy only applies to ${pct}% of mail — the remaining ${100 - pct}% has no enforcement. Raise pct to 100 to close the gap.`,
          ctaLabel: simple ? 'Cover all your mail →' : 'Raise pct to 100 →',
          ctaTarget: ctaDns,
        }
      }
      if (overrideGroup === 'fwd_list') {
        return {
          stripCopy: simple
            ? 'This was a forwarded or mailing-list message — mail providers often relax protection for those. This is expected; there is nothing to fix here.'
            : "Your policy says 'block', but this got through anyway. It was a forwarded or mailing-list message — providers typically relax DMARC enforcement for those cases. This is expected behavior; no policy change will fix it.",
          ctaLabel: null,
          ctaTarget: null,
        }
      }
      if (overrideGroup === 'local') {
        return {
          stripCopy: simple
            ? `Your blocking protection is on, but ${provider} used its own rules to deliver this anyway. This is rare — nothing to change on your end; it's ${provider}'s decision.`
            : `Your policy says 'block', but ${provider} delivered this using its own rules. This is rare — ${provider} overrode your DMARC policy. Nothing to change on your end; this is ${provider}'s decision.`,
          ctaLabel: null,
          ctaTarget: null,
        }
      }
      // pct=100, no override
      return {
        stripCopy: simple
          ? "Your blocking protection is fully on, but this provider delivered it anyway. This is rare and out of your control — there's nothing to change on your end."
          : "Your policy says 'block' (p=reject), but this provider delivered it anyway. This is rare — the provider may not be fully enforcing DMARC. There's no DNS change that fixes this; it's on the provider's side.",
        ctaLabel: null,
        ctaTarget: null,
      }
    }
    if (disposition === 'quarantine') {
      if (pct < 100) {
        return {
          stripCopy: simple
            ? "Your blocking protection doesn't cover all your mail yet — this landed in spam instead of being blocked outright. Cover all of it for full blocking."
            : `Your policy says 'block' (p=reject), but this landed in spam instead. Your policy only applies to ${pct}% of mail. Raise pct to 100 to enforce full blocking.`,
          ctaLabel: simple ? 'Cover all your mail →' : 'Raise pct to 100 →',
          ctaTarget: ctaDns,
        }
      }
      if (overrideGroup === 'fwd_list') {
        return {
          stripCopy: simple
            ? "Forwarding or a mailing list caused this to land in spam instead of being blocked. This is expected — forwarded mail often can't be fully blocked without breaking delivery."
            : "Your policy says 'block', but forwarding or a mailing list caused the provider to quarantine instead of block. This is expected — forwarded mail often can't be fully rejected without breaking delivery.",
          ctaLabel: null,
          ctaTarget: null,
        }
      }
      // local_policy is intentionally grouped with no-override here — matching
      // the approved UX decision table (p=reject, quarantine, pct=100, local|none row).
      // Both cases produce the same "treat as quarantine" explanation and no CTA.
      return {
        stripCopy: simple
          ? 'Your blocking protection is on, but this provider put it in spam instead of blocking it outright. Some providers do this — still better than the inbox, just not full blocking.'
          : "Your policy says 'block' (p=reject), but this provider put it in spam instead of blocking it. Some providers treat 'reject' as 'quarantine' — still better than the inbox, just not what you asked for.",
        ctaLabel: null,
        ctaTarget: null,
      }
    }
  }

  // Fallback: treat unknown policy/disposition as p=none, disposition=none
  return {
    stripCopy: simple
      ? "This fake email got through because your protection isn't turned on yet. Turn it on to block these automatically."
      : "This sender failed every authentication check, but your 'monitor only' policy (p=none) lets it through. Gmail, Outlook, and every other provider will keep delivering these until you tell them to stop.",
    ctaLabel: simple ? SIMPLE_TURN_ON_BLOCKING_CTA : 'Switch to p=reject →',
    ctaTarget: ctaDns,
  }
}

// ─── Context-aware disposition badge ──────────────────────────────────────────

export type DispositionBadgeData = {
  label: string
  /** CSS class modifier (in disposition-badge namespace) */
  cssModifier: string
  tooltipHuman: string
  tooltipSeverity: 'success' | 'warning' | 'error' | 'neutral'
}

/**
 * Returns badge label, CSS modifier, and tooltip text that correctly reflect
 * context: whether the row is an impostor changes what each outcome *means*.
 *
 * For non-impostor rows the output is identical to the previous static mapping.
 * For impostor rows the badge is inverted: reject = green win, none/quarantine = amber danger.
 */
export function getContextualDispositionBadge(
  disposition: string,
  isImpostor: boolean,
  reporterOrg: { String: string; Valid: boolean } | undefined,
  policyP: { String: string; Valid: boolean } | undefined,
  /** DKIM/SPF (policy_evaluated) results — used to give disposition=none a
   * positive framing when DMARC actually passed, instead of the generic
   * "no action taken" copy which reads as a gap even for clean mail. */
  authResults?: { dkim?: string; spf?: string },
  /**
   * D1: Simple mode drops all policy jargon (`p=`/`pct`/"policy_evaluated"/
   * "aligned") from the tooltip and uses a plainer badge label for
   * non-impostor rows. Expert output is byte-for-byte unchanged — this is
   * additive gating, never a lossy rewrite (Rafał still gets everything he
   * had). Defaults to 'expert' so any caller that hasn't been threaded
   * through yet keeps today's behavior.
   */
  viewMode: ViewMode = 'expert'
): DispositionBadgeData {
  const d = disposition?.toLowerCase() ?? ''
  const provider =
    reporterOrg?.Valid && reporterOrg.String
      ? reporterOrg.String
      : 'This provider'
  const policy = policyP?.Valid && policyP.String ? policyP.String : 'none'
  const simple = viewMode === 'simple'

  if (!isImpostor) {
    switch (d) {
      case 'none': {
        const dmarcPassed =
          authResults?.dkim === 'pass' || authResults?.spf === 'pass'
        return {
          label: simple ? '✅ Delivered' : getDispositionLabel('none'),
          cssModifier: 'disposition-none',
          tooltipHuman: simple
            ? dmarcPassed
              ? 'This email was delivered normally.'
              : "We can't tell yet whether this was a problem — it went through without a block."
            : dmarcPassed
              ? 'This email passed DMARC authentication (SPF or DKIM aligned) and was delivered normally — no policy action was needed.'
              : getDispositionExplanation('none'),
          tooltipSeverity: 'success',
        }
      }
      case 'quarantine':
        return {
          label: simple ? '⚠️ Sent to spam' : getDispositionLabel('quarantine'),
          cssModifier: 'disposition-quarantine',
          tooltipHuman: simple
            ? "This email went to the recipient's spam folder."
            : getDispositionExplanation('quarantine'),
          tooltipSeverity: 'warning',
        }
      case 'reject':
        return {
          label: simple ? '❌ Blocked' : getDispositionLabel('reject'),
          cssModifier: 'disposition-reject',
          tooltipHuman: simple
            ? 'This email never reached the recipient.'
            : getDispositionExplanation('reject'),
          tooltipSeverity: 'error',
        }
      default:
        return {
          label: disposition || 'Unknown',
          cssModifier: '',
          tooltipHuman: 'Unknown disposition status',
          tooltipSeverity: 'neutral',
        }
    }
  }

  // Impostor rows: invert the meaning
  switch (d) {
    case 'reject': {
      // WIN: blocked impostor — tooltip depends on the published policy
      let tooltipHuman: string
      if (simple) {
        tooltipHuman = 'Blocked — this fake email never reached anyone.'
      } else if (policy === 'none') {
        tooltipHuman = `Blocked by ${provider} — your policy is 'monitor only', so ${provider} acted on its own spam signals. Switch to p=reject so every provider blocks these, not just this one.`
      } else if (policy === 'quarantine') {
        tooltipHuman = `Blocked — ${provider} enforced stricter than your policy required. Switch to p=reject to make this the rule everywhere.`
      } else {
        tooltipHuman =
          'Blocked — exactly what your p=reject policy asks for. This sender never reached the inbox.'
      }
      return {
        label: '✅ Blocked ✓',
        cssModifier: 'disposition-impostor-reject',
        tooltipHuman,
        tooltipSeverity: 'success',
      }
    }
    case 'quarantine':
      return {
        label: '⚠️ Caught in spam — not stopped',
        cssModifier: 'disposition-impostor-quarantine',
        tooltipHuman: simple
          ? 'Caught in spam, but not fully blocked yet.'
          : 'An unauthenticated sender was filtered to spam, not blocked. See the note below to block it outright.',
        tooltipSeverity: 'warning',
      }
    case 'none':
      return {
        label: '⚠️ Went through — not blocked',
        cssModifier: 'disposition-impostor-none',
        tooltipHuman: simple
          ? 'This fake email went through unblocked.'
          : "An unauthenticated sender went through unblocked — your DMARC policy didn't stop it. Whether it landed in the inbox or spam is decided by the receiver's own filters, not your policy. See the note below to block these.",
        tooltipSeverity: 'warning',
      }
    default:
      // Unknown disposition value — neutral fallback, no impostor styling applied
      return {
        label: disposition || 'Unknown',
        cssModifier: '',
        tooltipHuman: 'Unknown disposition status',
        tooltipSeverity: 'neutral',
      }
  }
}

/**
 * Explain DKIM result
 */
export const getDKIMResultExplanation = (
  result: string,
  hasSignature: boolean
): string => {
  if (!hasSignature) {
    return 'No DKIM signature - Email was sent without DKIM signature. Configure DKIM signing on your mail server.'
  }

  switch (result?.toLowerCase()) {
    case 'pass':
      return 'DKIM signature verified successfully'
    case 'fail':
      return 'DKIM fail - DKIM signature was present but verification failed. Check DNS records and key configuration.'
    case 'policy':
      return "DKIM signature valid but key doesn't match domain policy. Check your DKIM key configuration and selector."
    case 'neutral':
      return 'Unable to verify DKIM signature. The verification result was inconclusive.'
    case 'temperror':
      return 'Temporary error during DKIM verification. This may resolve automatically.'
    case 'permerror':
      return 'Permanent error in DKIM configuration. Check your DNS records.'
    default:
      return `DKIM result: ${result}`
  }
}

/**
 * Explain SPF result
 */
export const getSPFResultExplanation = (result: string): string => {
  switch (result?.toLowerCase()) {
    case 'pass':
      return 'SPF authentication passed - sending server is authorized'
    case 'fail':
      return 'SPF fail - email sent from unauthorized server. Add the sending IP to your SPF record.'
    case 'softfail':
      return 'SPF soft fail - the sending server is not in your authorized list, but your policy allows delivery. Consider adding it to your SPF record if this server is legitimate.'
    case 'neutral':
      return 'SPF neutral - no explicit authorization policy'
    case 'none':
      return "No SPF record published - domain hasn't specified which servers can send email. Add an SPF record to your DNS."
    case 'temperror':
      return 'Temporary error during SPF check. This is usually a DNS issue and may resolve automatically.'
    case 'permerror':
      return 'Permanent error in SPF configuration. Your SPF record has syntax errors or exceeds DNS lookup limits.'
    default:
      return `SPF result: ${result}`
  }
}

/**
 * Explain alignment failure in simple terms
 */
export const getAlignmentExplanation = (type: 'dkim' | 'spf'): string => {
  if (type === 'dkim') {
    return "DKIM alignment failed - The domain in the DKIM signature doesn't match the sender's domain. This happens when emails are sent through third-party services."
  } else {
    return "SPF alignment failed - The SPF domain doesn't match the sender's domain. This often occurs with forwarded emails or third-party senders."
  }
}

/**
 * Get severity explanation
 */
export const getSeverityExplanation = (severity: string): string => {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return 'Critical - Possible email delivery issues. Immediate action required.'
    case 'warning':
      return 'Warning - Potential issues detected. Review recommended.'
    case 'info':
      return 'Info - For your awareness. No action required.'
    default:
      return severity
  }
}

/**
 * Explain problem types in user-friendly language
 */
export const getProblemTypeExplanation = (problemType: string): string => {
  switch (problemType) {
    case 'no-signature':
      return 'Email sent without DKIM signature'
    case 'dkim-fail':
      return 'DKIM signature verification failed'
    case 'spf-fail':
      return 'Email sent from unauthorized server'
    case 'alignment':
      return "Sender domains don't match each other"
    case 'multiple':
      return 'Multiple authentication issues detected'
    default:
      return problemType
  }
}

/**
 * Get action recommendation for problem type
 */
export const getProblemTypeAction = (problemType: string): string => {
  switch (problemType) {
    case 'no-signature':
      return 'Configure DKIM signing on your mail server or email service provider'
    case 'dkim-fail':
      return 'Verify DKIM DNS records and check key configuration'
    case 'spf-fail':
      return 'Add the sending IP address to your SPF record'
    case 'alignment':
      return 'Ensure all sender domains match, or adjust DMARC policy to relaxed mode'
    case 'multiple':
      return 'Review both SPF and DKIM configuration'
    default:
      return 'Review email authentication settings'
  }
}

/**
 * computeShowRuaMismatch determines whether DomainCommandCenter's "DMARC
 * reports are not being sent to DMARC Guardian" banner should render
 * (86cb0eqq6).
 *
 * canonicalRua is the domain's own backend-resolved RUA address
 * (DomainCommandCenter's domainEmails.rua, sourced from GET
 * /api/v1/domains/{id}/dns's `ruaEmail` field). Per emitRUAEmail's contract
 * (internal/api/dnsrec_serialize.go — invariant I1), the backend OMITS this
 * JSON key entirely, and NEVER emits an empty string, when it hasn't
 * resolved a report address for this domain yet (e.g. an older domain that
 * predates RUA generation). The frontend must not collapse "omitted" into
 * `''` and then compare every published address against that empty string
 * — nothing ever equals `''`, so that comparison always reports a mismatch
 * even when the published record is correct. "We don't know our own
 * address yet" and "the published record doesn't include it" are different
 * facts and must render different words:
 *
 *   - canonicalRua known, and IS among publishedRua     -> false (no mismatch)
 *   - canonicalRua known, and NOT among publishedRua    -> true  (genuine mismatch)
 *   - canonicalRua unknown (undefined or empty)         -> false (absence
 *     must never render as a mismatch, and a caller must not go on to
 *     interpolate an unknown address into user-facing copy)
 *
 * dnsValidationLoaded/hasDmarcRecord gate the banner exactly as before this
 * fix — this function only corrects the RUA-comparison half.
 */
export function computeShowRuaMismatch(
  canonicalRua: string | undefined,
  publishedRua: string[],
  dnsValidationLoaded: boolean,
  hasDmarcRecord: boolean
): boolean {
  if (!dnsValidationLoaded || !hasDmarcRecord) return false
  if (!canonicalRua) return false
  const target = canonicalRua.toLowerCase()
  return !publishedRua.some(
    (email) => email.replace(/^mailto:/i, '').toLowerCase() === target
  )
}

/**
 * Glossary of terms (for help/tooltip reference)
 */
export const DMARC_GLOSSARY = {
  'Email Security Policy (DMARC)':
    'A verification system that checks whether an email really comes from the stated sender',
  'Authorized Senders (SPF)':
    'A list of servers that are allowed to send emails on behalf of your domain',
  'Email Signature (DKIM)':
    'A digital signature that confirms the email has not been modified',
  Disposition:
    "What the receiver's policy did with the email: let it through unblocked, sent it to spam, or blocked it (DMARC reports don't say whether it reached the inbox or spam within a 'let through' outcome)",
  'Domain Match':
    'Checks that the "From" domain matches the domain used for SPF/DKIM — prevents spoofing',
  RUA: 'The email address where email providers send your DMARC reports',
  RUF: 'The address for forensic (failure) reports about individual emails. DMARC Guardian monitors aggregate reports (RUA) only — forensic reports are not analyzed.',
  Pass: 'Email passed verification successfully',
  Fail: 'Email did not pass verification',
  'Sent to spam folder': 'Email was moved to spam folder',
  'Blocked — never delivered':
    'Email was completely blocked and did not reach the recipient',
}
